// Messages — encrypted chat with assigned patients (D5). Two-pane inbox styled identically to the
// patient portal (.pt-inbox): conversation sidebar (Inbox header + square-pencil compose) + thread
// pane. The mockup's "save thread to patient chart" action is intentionally NOT built — copying chat
// into the record is forbidden (D5); the record lives in Healthie.
import "../styles-messages.css";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Modal } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { useUnreadApi } from "../lib/unreadApi";
import { formatDateTime, age } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import type { RosterPatient } from "@forcebase-ai/schema/doctor";

const ComposeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const BackIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
);
const initials = (p: { first_name: string; last_name: string }) =>
  `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase();

export function Messages() {
  const api = useApi();
  const [params] = useSearchParams();
  const roster = useAsync(() => api.patients(), []);
  const [active, setActive] = useState<string | null>(params.get("patient"));
  const [showNew, setShowNew] = useState(false);

  // Per-patient unread for the inbox rows (the published patients() strips `unread`, so read it raw).
  // Refetch when the open thread changes (opening marks it read → its row clears) + poll every 5s.
  const unreadApi = useUnreadApi();
  const [unread, setUnread] = useState<Record<string, number>>({});
  useEffect(() => {
    let on = true;
    const load = () => unreadApi.unreadByPatient().then((m) => on && setUnread(m)).catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => {
      on = false;
      clearInterval(iv);
    };
  }, [unreadApi, active]);

  if (roster.loading) return <Loading />;
  if (roster.error) return <ErrorState message={roster.error} />;

  const patients = roster.data ?? [];
  const currentId = active && patients.some((p) => p.id === active) ? active : patients[0]?.id ?? null;
  const current = patients.find((p) => p.id === currentId) ?? null;

  return (
    <>
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">Secure clinical messaging</div>
          <h1 className="dp-head__title">Messages.</h1>
        </div>
      </div>

      <div className={`pt-inbox ${active ? "m-chat" : ""}`}>
        {/* ===== Conversation sidebar ===== */}
        <aside className="pt-inbox__list">
          <div className="pt-inbox__top">
            <h3 className="pt-inbox__title">Inbox</h3>
            <button
              className="pt-compose-btn"
              onClick={() => setShowNew(true)}
              aria-label="Start a new message"
              title="New message"
            >
              {ComposeIcon}
            </button>
          </div>
          {patients.length ? (
            <div className="pt-threads">
              {patients.map((p) => {
                const u = unread[p.id] ?? 0;
                return (
                  <button
                    key={p.id}
                    className={`pt-thread ${currentId === p.id ? "is-active" : ""}`}
                    onClick={() => setActive(p.id)}
                  >
                    <span className="pt-thread__avatar">{initials(p)}</span>
                    <span className={`pt-thread__main ${u > 0 ? "has-unread" : ""}`}>
                      <span className="pt-thread__row">
                        <span className="pt-thread__name">{p.first_name} {p.last_name}</span>
                      </span>
                      <span className="pt-thread__preview">Secure thread</span>
                    </span>
                    {u > 0 && <span className="pt-thread__unread">{u > 99 ? "99+" : u}</span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="pt-threads pt-threads--empty">No assigned patients yet.</div>
          )}
        </aside>

        {/* ===== Active thread ===== */}
        <section className="pt-inbox__pane">
          {current ? (
            <Thread
              key={current.id}
              patientId={current.id}
              name={`${current.first_name} ${current.last_name}`}
              avatar={initials(current)}
              onBack={() => setActive(null)}
            />
          ) : (
            <div className="pt-chat__empty">Select a conversation.</div>
          )}
        </section>
      </div>

      {showNew && (
        <NewChatModal
          patients={patients}
          onClose={() => setShowNew(false)}
          onPick={(id) => { setShowNew(false); setActive(id); }}
        />
      )}
    </>
  );
}

function Thread({
  patientId,
  name,
  avatar,
  onBack,
}: {
  patientId: string;
  name: string;
  avatar: string;
  onBack: () => void;
}) {
  const api = useApi();
  // Poll so the patient's messages appear without a manual refresh.
  const { data, loading, error, reload } = useAsync(() => api.messages(patientId), [patientId], {
    pollMs: 5000,
  });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const messages = data?.messages ?? [];

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  async function send() {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await api.sendMessage(patientId, draft.trim());
      setDraft("");
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="pt-chat__head">
        <button className="pt-chat__back" onClick={onBack} aria-label="Back to conversations">
          {BackIcon}
        </button>
        <span className="pt-chat__avatar">{avatar}</span>
        <div className="pt-chat__head-main">
          <div className="pt-chat__who">{name}</div>
          <div className="pt-chat__status">Patient · secure &amp; encrypted</div>
        </div>
        <Link to={`/patients/${patientId}`} className="pt-chat__view">View patient &rarr;</Link>
      </div>

      {loading && !data ? (
        <div className="pt-chat__empty"><Loading /></div>
      ) : error ? (
        <div className="pt-chat__empty"><ErrorState message={error} /></div>
      ) : messages.length ? (
        <div className="pt-chat__list" ref={listRef}>
          {messages.map((m) => (
            <div key={m.id} className={`pt-msg pt-msg--${m.sender_role}`}>
              <div className="pt-msg__bubble">{m.body}</div>
              <div className="pt-msg__time">{formatDateTime(m.created_at)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="pt-chat__empty">No messages yet with {name}.</div>
      )}

      <div className="pt-chat__compose">
        <input
          className="pt-chat__input"
          // land ready to type when a thread opens
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Write a message…"
          aria-label="Message"
        />
        <button className="pt-chat__send" disabled={!draft.trim() || busy} onClick={send} aria-label="Send">
          {SendIcon}
        </button>
      </div>
    </>
  );
}

function NewChatModal({
  patients,
  onClose,
  onPick,
}: {
  patients: RosterPatient[];
  onClose: () => void;
  onPick: (patientId: string) => void;
}) {
  return (
    <Modal open onClose={onClose} labelledBy="newChatTitle">
      <div className="pt-newchat-title" id="newChatTitle">
        New <em>message</em>
      </div>
      <p className="pt-newchat-sub">Choose a patient to open a secure conversation.</p>

      {patients.length ? (
        <div className="pt-doc-picker">
          {patients.map((p) => (
            <button key={p.id} type="button" className="pt-doc-card" onClick={() => onPick(p.id)}>
              <span className="pt-doc-card__avatar">{initials(p)}</span>
              <span className="pt-doc-card__main">
                <span className="pt-doc-card__name">{p.first_name} {p.last_name}</span>
                <span className="pt-doc-card__spec">{age(p.dob)} yrs</span>
              </span>
              <span className="pt-doc-card__go">&rarr;</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="pt-newchat-empty">No assigned patients yet.</div>
      )}
    </Modal>
  );
}
